import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, Clock, Copy, ArrowRight, ShieldCheck, MessageCircle, Smartphone, AlertTriangle, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

const statusMeta = {
  awaiting_payment: { label: "Awaiting payment", cls: "bg-yellow-500/15 text-yellow-400 border-yellow-500/25", Icon: Clock },
  verifying: { label: "Verifying payment", cls: "bg-blue-500/15 text-blue-400 border-blue-500/25", Icon: ShieldCheck },
  paid: { label: "Confirmed", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25", Icon: Check },
  delivered: { label: "Delivered", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/25", Icon: Check },
  failed: { label: "Failed", cls: "bg-red-500/15 text-red-400 border-red-500/25", Icon: XCircle },
  cancelled: { label: "Cancelled", cls: "bg-white/5 text-kyro-mute border-white/10", Icon: XCircle },
};

function CopyRow({ label, value, testId }) {
  if (!value) return null;
  return (
    <div className="kyro-card p-4">
      <div className="text-[10px] uppercase tracking-widest text-kyro-mute mb-1">{label}</div>
      <div className="flex items-center justify-between gap-2">
        <div className="font-mono text-sm md:text-base break-all" data-testid={testId}>{value}</div>
        <button
          onClick={() => { navigator.clipboard.writeText(value); toast.success(`${label} copied`); }}
          className="w-8 h-8 grid place-items-center rounded-full hover:bg-white/5 shrink-0"
          data-testid={`${testId}-copy`}
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function OrderConfirmPage() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const emailParam = params.get("email") || localStorage.getItem("kyro_last_email") || "";
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [settings, setSettings] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const load = async () => {
    try {
      const q = emailParam ? { email: emailParam } : {};
      const { data } = await api.get(`/orders/${orderId}`, { params: q });
      setOrder(data);
    } catch (e) { toast.error("Order not found or access denied"); }
  };

  useEffect(() => {
    load();
    api.get("/settings/payment").then((r) => setSettings(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    if (!order) return;
    if (["paid", "delivered", "cancelled", "failed"].includes(order.status)) return;
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.status]);

  const markPaid = async () => {
    setSubmitting(true);
    try {
      await api.post(`/orders/${orderId}/mark-paid`, { email: emailParam || undefined });
      toast.success("Thanks — we'll verify and deliver within 1–2 hours");
      setConfirm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  const cancelOrder = async () => {
    if (!window.confirm("Cancel this order? You can place a new one anytime.")) return;
    setCancelling(true);
    try {
      await api.post(`/orders/${orderId}/cancel`, { email: emailParam || undefined });
      toast.success("Order cancelled");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to cancel");
    } finally { setCancelling(false); }
  };

  if (!order || !settings) return <div className="min-h-[60vh] grid place-items-center text-kyro-mute">Loading...</div>;

  const st = statusMeta[order.status] || statusMeta.awaiting_payment;
  const StIcon = st.Icon;
  const isAwaiting = order.status === "awaiting_payment";
  const isVerifying = order.status === "verifying";
  const isPaid = ["paid", "delivered"].includes(order.status);
  const isCancelled = order.status === "cancelled";
  const anyPaymentInfo = !!(settings.upi_id || settings.upi_qr_url || settings.gpay_number || settings.phonepe_number);

  const upiDeepLink = settings.upi_id
    ? `upi://pay?pa=${encodeURIComponent(settings.upi_id)}&pn=${encodeURIComponent(settings.payee_name || "BABA MEDIA")}&am=${order.total.toFixed(2)}&cu=INR&tn=${encodeURIComponent(order.id)}`
    : "";

  const whatsappLink = settings.whatsapp_number
    ? `https://wa.me/${settings.whatsapp_number}?text=${encodeURIComponent(`Hi Baba Media, I've placed order ${order.id} for ₹${order.total.toFixed(0)}. Please assist.`)}`
    : "";

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-8 py-14">
      <div className="text-center mb-10">
        <div className={`inline-grid place-items-center w-16 h-16 rounded-full mb-4 ${st.cls}`}>
          <StIcon className="w-6 h-6" />
        </div>
        <div className="kyro-overline">{st.label}</div>
        <h1 className="font-display font-black text-3xl md:text-5xl tracking-tighter mt-3">
          {isAwaiting && "Complete your payment"}
          {isVerifying && "Payment received — verifying"}
          {isPaid && "Order confirmed"}
          {isCancelled && "Order cancelled"}
          {order.status === "failed" && "Payment failed"}
        </h1>
        <p className="text-kyro-mute mt-3">Order <span className="font-mono text-kyro-ink">{order.id}</span></p>
      </div>

      {/* PAYMENT — only when awaiting */}
      {isAwaiting && (
        <div className="kyro-card p-6 md:p-10 mb-6">
          {!anyPaymentInfo ? (
            <div className="text-center py-6">
              <p className="text-kyro-mute">Payment methods are being set up. Please contact us on WhatsApp to complete your order.</p>
              {whatsappLink && (
                <a href={whatsappLink} target="_blank" rel="noreferrer" className="kyro-btn-primary inline-flex items-center gap-2 mt-4" data-testid="wa-fallback-btn"><MessageCircle className="w-4 h-4" /> Chat on WhatsApp</a>
              )}
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 gap-8 items-start">
                {/* QR side */}
                <div className="text-center">
                  <div className="kyro-overline mb-4">Scan & pay ₹{order.total.toFixed(0)}</div>
                  {settings.upi_qr_url ? (
                    <div className="inline-block p-4 bg-white rounded-2xl">
                      <img src={settings.upi_qr_url} alt="UPI QR" className="w-56 h-56 object-contain" data-testid="upi-qr-image" />
                    </div>
                  ) : (
                    <div className="w-56 h-56 mx-auto rounded-2xl bg-white/5 border border-white/10 grid place-items-center text-kyro-mute text-sm px-6 text-center">
                      QR image not uploaded yet — use the numbers on the right.
                    </div>
                  )}
                  {upiDeepLink && (
                    <div className="mt-4">
                      <a href={upiDeepLink} className="kyro-btn-secondary text-sm inline-flex items-center gap-2" data-testid="upi-open-app-btn">Open in UPI app <ArrowRight className="w-3.5 h-3.5" /></a>
                    </div>
                  )}
                </div>

                {/* Payment IDs side */}
                <div>
                  <div className="kyro-overline mb-4">Pay to any of these</div>
                  <div className="space-y-3">
                    <div className="kyro-card p-4 border-[#FF4433]/30 bg-[#FF4433]/5">
                      <div className="text-[10px] uppercase tracking-widest text-kyro-mute mb-1">Amount</div>
                      <div className="font-display font-black text-3xl text-[#FF4433]">₹{order.total.toFixed(0)}</div>
                    </div>
                    <CopyRow label="UPI ID" value={settings.upi_id} testId="upi-id-text" />
                    <CopyRow label="GPay number" value={settings.gpay_number} testId="gpay-number-text" />
                    <CopyRow label="PhonePe number" value={settings.phonepe_number} testId="phonepe-number-text" />
                    <div className="kyro-card p-4">
                      <div className="text-[10px] uppercase tracking-widest text-kyro-mute mb-1">Payee</div>
                      <div className="font-semibold text-sm">{settings.payee_name || "BABA MEDIA"}</div>
                    </div>
                  </div>
                </div>
              </div>

              {settings.instructions && (
                <div className="mt-6 p-4 rounded-xl bg-white/5 border border-white/10 text-sm text-kyro-mute leading-relaxed">
                  {settings.instructions}
                </div>
              )}

              {/* WARNING */}
              {settings.warning_text && (
                <div className="mt-4 p-4 rounded-xl border border-red-500/40 bg-red-500/10">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                    <p className="text-sm text-red-100 leading-relaxed font-medium" data-testid="payment-warning">{settings.warning_text}</p>
                  </div>
                </div>
              )}

              {/* CTAs */}
              <div className="mt-8 grid sm:grid-cols-2 gap-3">
                <button
                  data-testid="mark-paid-btn"
                  onClick={() => setConfirm(true)}
                  disabled={submitting}
                  className="kyro-btn-primary flex items-center justify-center gap-2 py-4 text-base"
                >
                  <Check className="w-5 h-5" /> I've paid — verify my order
                </button>
                <button
                  data-testid="cancel-order-btn"
                  onClick={cancelOrder}
                  disabled={cancelling}
                  className="kyro-btn-secondary flex items-center justify-center gap-2 py-4 text-base"
                >
                  <XCircle className="w-5 h-5" /> Cancel order
                </button>
              </div>

              {whatsappLink && (
                <div className="mt-4 text-center">
                  <a href={whatsappLink} target="_blank" rel="noreferrer" className="text-sm text-kyro-mute hover:text-[#FF4433] inline-flex items-center gap-1"><MessageCircle className="w-4 h-4" /> Facing issues? Chat with us on WhatsApp</a>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Confirm modal */}
      {confirm && (
        <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm grid place-items-center p-4">
          <div className="kyro-card w-full max-w-md p-6 md:p-8">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
              <div>
                <h3 className="font-display font-bold text-lg">Confirm you've paid</h3>
                <p className="text-sm text-kyro-mute mt-1">By clicking confirm, you declare that you have already sent ₹{order.total.toFixed(0)} to the details shown. False claims will result in your account being blocked from all future orders and legal action.</p>
              </div>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button onClick={() => setConfirm(false)} className="kyro-btn-secondary text-sm">Not yet</button>
              <button onClick={markPaid} disabled={submitting} className="kyro-btn-primary text-sm inline-flex items-center gap-2" data-testid="confirm-paid-btn">
                {submitting ? "Submitting..." : <>Yes, I've paid <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* VERIFYING */}
      {isVerifying && (
        <div className="kyro-card p-6 md:p-10 mb-6">
          <div className="flex items-start gap-4">
            <ShieldCheck className="w-6 h-6 text-blue-400 mt-1 shrink-0" />
            <div>
              <p className="font-semibold">Thanks — verifying payment</p>
              <p className="text-sm text-kyro-mute mt-2">Our team will check the incoming payment against your order and confirm within 1–2 hours during business hours. You'll receive delivery details on <span className="text-kyro-ink">{order.guest_email}</span>.</p>
              {whatsappLink && (
                <a href={whatsappLink} target="_blank" rel="noreferrer" className="kyro-btn-secondary text-sm mt-4 inline-flex items-center gap-2" data-testid="wa-verify-btn"><MessageCircle className="w-4 h-4" /> Need help? Chat on WhatsApp</a>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PAID */}
      {isPaid && order.delivery_notes && (
        <div className="p-6 md:p-8 rounded-2xl bg-emerald-500/5 border border-emerald-500/20 mb-6">
          <div className="text-[10px] font-bold uppercase tracking-widest text-emerald-400 mb-3">Access credentials</div>
          <pre className="text-sm whitespace-pre-wrap font-mono">{order.delivery_notes}</pre>
        </div>
      )}
      {isPaid && !order.delivery_notes && (
        <div className="kyro-card p-6 md:p-8 mb-6 text-center">
          <p className="text-kyro-mute">Payment confirmed. Our team is preparing your subscription — you'll receive credentials shortly.</p>
        </div>
      )}

      {isCancelled && (
        <div className="kyro-card p-6 md:p-8 mb-6 text-center">
          <p className="text-kyro-mute">This order was cancelled. Feel free to place a new one anytime.</p>
          <Link to="/shop" className="kyro-btn-primary text-sm inline-flex items-center gap-2 mt-4">Continue shopping <ArrowRight className="w-4 h-4" /></Link>
        </div>
      )}

      {/* Order summary */}
      <div className="kyro-card p-6 md:p-8">
        <div className="kyro-overline mb-4">Order items</div>
        <div className="space-y-3">
          {order.items.map((it, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-md bg-white/5 overflow-hidden shrink-0">
                {it.product_image && <img src={it.product_image} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold truncate">{it.product_name}</p>
                <p className="text-xs text-kyro-mute">{it.plan_label}</p>
              </div>
              <p className="font-semibold">₹{it.price.toFixed(0)}</p>
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-6 pt-5 border-t border-white/5">
          <p className="text-kyro-mute">Total</p>
          <p className="font-display font-black text-xl">₹{order.total.toFixed(0)}</p>
        </div>
      </div>

      <div className="mt-8 flex flex-wrap gap-3 justify-center">
        <button data-testid="copy-order-id-btn" onClick={() => { navigator.clipboard.writeText(order.id); toast.success("Order ID copied"); }} className="kyro-btn-secondary inline-flex items-center gap-2 text-sm"><Copy className="w-4 h-4" /> Copy order ID</button>
        {user ? (
          <Link to="/account" className="kyro-btn-primary inline-flex items-center gap-2 text-sm">My orders <ArrowRight className="w-4 h-4" /></Link>
        ) : (
          <Link to="/track" className="kyro-btn-primary inline-flex items-center gap-2 text-sm">Track your order <ArrowRight className="w-4 h-4" /></Link>
        )}
      </div>
    </div>
  );
}
